import { useCallback, useEffect, useMemo, useState, type MutableRefObject } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { closeCashSession, openCashSession } from '../../infra/cashSessionsRepo'
import type { CashSummary } from '../../infra/cashSummaryQuery';
import {
  bumpCashSessionChangeSignal,
} from '../../infra/cashSessionChangeSignal';
import { invalidateCashSummaryCaches, resolveCashSummary } from '../../infra/resolveCashSummary';
import { invalidateReportsCache } from '../../../reports/remote-api';
import { mapCashCommandError, rawCashErrorText } from '../../infra/mapCashCommandError';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import type { OperationalConfirmOptions } from '../../../shared/ui/useOperationalConfirm';
import { useCashOutflows } from './useCashOutflows';
import { useCashActivityFeed } from './useCashActivityFeed';
import { listActiveSuppliersLocal } from '../../../catalog/infra-api';
import { useExpenseRegisterSubmit } from '../../../expenses/screens/hooks/useExpenseRegisterSubmit';
import type { ActiveCashSessionInfo } from '../../../expenses/components/expensePaymentFlow';
import type { EmployeeRecord } from '../../../team/store/useEmployeesStore';
import { resolveCashOperatorLabel } from '../cashOperatorLabels';
import {
  cashArqueoCopy,
  formatCashDifferenceConfirmMessage,
  formatOpeningConfirmMessage,
} from '../workspace/cashArqueoCopy';
import { requireBranchSupervisorPin, authorizeSupervisorPin } from '../../../shell/session/supervisorPinApi';

function presentCashCommandFailure(params: {
  op: string;
  error: unknown;
  title: string;
}): void {
  const mapped = mapCashCommandError(rawCashErrorText(params.error));
  if (mapped.expected) {
    showError(params.title, mapped.userMessage);
    return;
  }
  presentUnexpectedFailure({
    module: 'cash',
    op: params.op,
    error: params.error,
    title: params.title,
    userMessage: mapped.userMessage,
  });
}

/** Efectivo esperado del summary fresco (server preferred). */
function expectedFromSummary(summary: CashSummary | null): number | null {
  if (!summary?.activeSession) return null;
  if (summary.expectedBalance != null) return summary.expectedBalance;
  return (
    (summary.activeSession.openingAmount ?? 0) +
    (summary.sessionCashTotal ?? 0) -
    (summary.sessionOutflowsTotal ?? 0)
  );
}

/** Parseo de monto contado (convención es-AR). Vacío o inválido → error explícito. */
function parseCountedCashInput(input: string): { ok: true; value: number } | { ok: false; reason: 'empty' | 'invalid' } {
  if (input.trim() === '') return { ok: false, reason: 'empty' };
  const n = parseDecimalInput(input, { min: 0, money: true });
  if (n === null) return { ok: false, reason: 'invalid' };
  return { ok: true, value: n };
}

type Params = {
  currentBranchId: string | null | undefined;
  userId: string | null | undefined;
  userEmail: string | null | undefined;
  canEdit: boolean;
  canEditExpenses: boolean;
  canView: boolean;
  canVoid: boolean;
  canMovement?: boolean;
  canCorrect?: boolean;
  isOwner?: boolean;
  cashSummary: CashSummary | null;
  applyCashSummaryState: (local: CashSummary, merged: CashSummary) => void;
  refreshCashSummary: (options?: { force?: boolean }) => Promise<void>;
  loadDifferencesFirstPage: () => Promise<void>;
  reloadHistoryRef: MutableRefObject<() => void | Promise<void>>;
  expectedCashInDrawer: number | null;
  organizationId: string | null;
  cashOutflowsEnabled: boolean;
  expensesModuleEnabled: boolean;
  employees: EmployeeRecord[];
  confirm: (options: OperationalConfirmOptions) => Promise<boolean>;
  promptSupervisorPin?: (opts?: { title?: string; message?: string }) => Promise<string | null>;
};

export function useCashManagementActions({
  currentBranchId,
  userId,
  userEmail,
  canEdit,
  canEditExpenses,
  canView,
  canVoid,
  canMovement = canEdit,
  canCorrect = canEdit,
  isOwner = false,
  cashSummary,
  applyCashSummaryState,
  refreshCashSummary,
  loadDifferencesFirstPage,
  reloadHistoryRef,
  expectedCashInDrawer,
  organizationId,
  cashOutflowsEnabled,
  expensesModuleEnabled,
  employees,
  confirm,
  promptSupervisorPin,
}: Params) {
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [closingNote, setClosingNote] = useState('');
  const [openingCash, setOpeningCash] = useState(false);
  const [closingCash, setClosingCash] = useState(false);

  useEffect(() => {
    if (cashSummary?.activeSession || openingAmount !== '') return;
    const lastAmount = cashSummary?.lastClosedSession?.closingAmount;
    if (lastAmount != null) {
      setOpeningAmount(String(lastAmount));
    }
  }, [cashSummary?.activeSession?.id, cashSummary?.lastClosedSession?.id, cashSummary?.lastClosedSession?.closingAmount, openingAmount, cashSummary?.activeSession]);

  const resetCloseCashForm = useCallback(() => {
    setClosingAmount('');
    setClosingNote('');
  }, []);

  const outflowsFlow = useCashOutflows({
    branchId: currentBranchId ?? null,
    sessionId: cashSummary?.activeSession?.id,
    organizationId,
    enabled: cashOutflowsEnabled && Boolean(cashSummary?.activeSession),
    canEdit: canMovement,
    canVoid,
    isOwner,
    userId: userId ?? undefined,
    expectedCashInDrawer,
    onChanged: refreshCashSummary,
    confirm,
    promptSupervisorPin,
  });

  const [expenseRegisterSuppliers, setExpenseRegisterSuppliers] = useState<
    Array<{ id: string; name: string }>
  >([]);

  useEffect(() => {
    if (!expensesModuleEnabled) {
      setExpenseRegisterSuppliers([]);
      return;
    }
    void listActiveSuppliersLocal()
      .then(setExpenseRegisterSuppliers)
      .catch(() => setExpenseRegisterSuppliers([]));
  }, [expensesModuleEnabled]);

  const activeCashSessionInfo = useMemo((): ActiveCashSessionInfo | null => {
    const session = cashSummary?.activeSession;
    if (!session) return null;
    return {
      session,
      operatorLabel: resolveCashOperatorLabel(
        session.openedBy,
        employees,
        userId ?? null,
        userEmail
      ),
    };
  }, [cashSummary?.activeSession, employees, userEmail, userId]);

  const refreshAfterExpenseRegister = useCallback(async () => {
    await outflowsFlow.reload();
    bumpCashSessionChangeSignal();
  }, [outflowsFlow]);

  const expenseRegisterSubmit = useExpenseRegisterSubmit({
    canEdit: canEditExpenses,
    organizationId,
    branchId: currentBranchId ?? null,
    activeCashSessionId: cashSummary?.activeSession?.id ?? null,
    userId: userId ?? undefined,
    expectedCashInDrawer,
    onSuccess: refreshAfterExpenseRegister,
    onClose: () => outflowsFlow.setRegisterOpen(false),
    confirm,
  });

  const handleOpenRegisterOutflow = useCallback(() => {
    if (expensesModuleEnabled) {
      if (!canEditExpenses) {
        showError('Sin permisos', 'Tu perfil no puede registrar gastos. Pedí permiso de Gastos.');
        return;
      }
    } else if (!canMovement) {
      showError('Sin permisos', 'Tu perfil no puede registrar egresos.');
      return;
    }
    outflowsFlow.openRegister('out');
  }, [expensesModuleEnabled, canEditExpenses, canMovement, outflowsFlow]);

  const handleOpenRegisterInflow = useCallback(() => {
    if (!canMovement) {
      showError('Sin permisos', 'Tu perfil no puede registrar ingresos.');
      return;
    }
    outflowsFlow.openRegister('in');
  }, [canMovement, outflowsFlow]);

  const activityFlow = useCashActivityFeed({
    branchId: currentBranchId ?? null,
    enabled: canView,
  });

  const handleOpenCash = async () => {
    if (!currentBranchId) {
      showError('Sucursal requerida', 'Seleccioná una sucursal para abrir la caja.');
      return;
    }
    if (!canEdit) {
      showError('Sin permisos', 'Tu perfil no puede abrir ni cerrar caja.');
      return;
    }

    const parsed = parseCountedCashInput(openingAmount);
    if (!parsed.ok) {
      if (parsed.reason === 'empty') {
        showError('Falta el fondo inicial', 'Ingresá el monto o 0 si abrís sin efectivo en caja.');
      } else {
        showError('Monto inválido', 'Usá solo números y una coma o punto para decimales.');
      }
      return;
    }
    const amount = parsed.value;

    const confirmed = await confirm({
      title: cashArqueoCopy.openConfirmTitle,
      message: formatOpeningConfirmMessage(amount),
      confirmLabel: cashArqueoCopy.openConfirmLabel,
      iconName: 'lock-open-outline',
    });
    if (!confirmed) return;

    setOpeningCash(true);
    try {
      const openResult = await openCashSession({
        branchId: currentBranchId,
        openingAmount: amount,
        openedBy: userId ?? undefined,
      });
      setClosingAmount('');
      setClosingNote('');
      invalidateCashSummaryCaches(currentBranchId);
      const afterOpen = await resolveCashSummary(currentBranchId, { force: true });
      applyCashSummaryState(afterOpen, afterOpen);
      bumpCashSessionChangeSignal();
      if (!afterOpen.activeSession) {
        showError(
          'Hay un turno en el servidor',
          'Actualizá la pantalla. Si ves la caja abierta, cerrala antes de abrir otra.'
        );
        return;
      }
      if (openResult.alreadyOpen) {
        showInfo(
          'Ya había un turno abierto',
          'Adoptamos el turno de la sucursal. Ya podés cobrar.'
        );
      } else {
        showSuccess('Caja abierta', 'Ya podés cobrar.');
      }
    } catch (error: unknown) {
      presentCashCommandFailure({
        op: 'open_cash',
        error,
        title: 'No se pudo abrir la caja',
      });
    } finally {
      setOpeningCash(false);
    }
  };

  const runCloseCash = async (
    amount: number,
    supervisorOverrideId?: string,
    freshSummary?: CashSummary | null
  ) => {
    if (!cashSummary?.activeSession) return;
    const activeSession = cashSummary.activeSession;
    const sessionId = activeSession.id;
    const branchId = currentBranchId;
    setClosingCash(true);
    try {
      const result = await closeCashSession({
        sessionId,
        closingAmount: amount,
        note: (closingNote ?? '').trim() || undefined,
        closedBy: userId ?? undefined,
        supervisorOverrideId,
        // Evita doble resolución del summary: handleCloseCash ya forzó un fetch fresco.
        summary: freshSummary ?? undefined,
      });
      setClosingAmount('');
      setClosingNote('');
      setOpeningAmount('');
      invalidateCashSummaryCaches(branchId!);
      invalidateReportsCache('reports_branch_cash_closures');
      const afterClose = await resolveCashSummary(branchId!, { force: true });
      applyCashSummaryState(afterClose, afterClose);
      bumpCashSessionChangeSignal();
      await loadDifferencesFirstPage();
      await reloadHistoryRef.current();
      if (result.alreadyClosed) {
        showInfo('Turno ya cerrado', 'Ese turno ya estaba cerrado. Actualizá la pantalla.');
      } else {
        showSuccess('Caja cerrada', 'Quedó guardado en Historial.');
      }
    } catch (error: unknown) {
      presentCashCommandFailure({
        op: 'close_cash',
        error,
        title: 'No se pudo cerrar la caja',
      });
    } finally {
      setClosingCash(false);
    }
  };

  const handleCloseCash = async (): Promise<boolean> => {
    if (!cashSummary?.activeSession) {
      showError('Sin caja activa', 'No hay una caja abierta para cerrar.');
      return false;
    }
    if (!canEdit) {
      showError('Sin permisos', 'Tu perfil no puede abrir ni cerrar caja.');
      return false;
    }
    if (closingCash) return false;
    if (!currentBranchId) {
      showError('Sucursal requerida', 'Seleccioná una sucursal para cerrar la caja.');
      return false;
    }

    const parsed = parseCountedCashInput(closingAmount);
    if (!parsed.ok) {
      if (parsed.reason === 'empty') {
        showError(cashArqueoCopy.missingCountedTitle, cashArqueoCopy.missingCountedMessage);
      } else {
        showError('Monto inválido', 'Usá solo números y una coma o punto para decimales.');
      }
      return false;
    }
    const amount = parsed.value;

    // Snapshot fresco antes de PIN/confirm — evita drift vs server v_difference
    let gateExpected = expectedCashInDrawer;
    let gateSessionId = cashSummary.activeSession.id;
    let freshSummary: CashSummary | null = null;
    try {
      const fresh = await resolveCashSummary(currentBranchId, { force: true });
      applyCashSummaryState(fresh, fresh);
      if (!fresh.activeSession || fresh.activeSession.status !== 'open') {
        showError('Sin caja activa', 'No hay una caja abierta para cerrar. Actualizá la pantalla.');
        return false;
      }
      freshSummary = fresh;
      gateSessionId = fresh.activeSession.id;
      gateExpected = expectedFromSummary(fresh);
    } catch (error: unknown) {
      presentCashCommandFailure({
        op: 'close_cash_refresh',
        error,
        title: 'No se pudo actualizar el arqueo',
      });
      return false;
    }

    const difference = gateExpected != null ? amount - gateExpected : null;

    if (difference != null && difference !== 0) {
      let supervisorOverrideId: string | undefined;
      if (!canCorrect && !isOwner) {
        if (!promptSupervisorPin) {
          showError('Sin permisos', 'Tu perfil no puede cerrar caja con diferencia. Pedí permiso de corregir o PIN de supervisor.');
          return false;
        }
        try {
          await requireBranchSupervisorPin(currentBranchId);
        } catch (error: unknown) {
          presentUnexpectedFailure({ module: 'cash', op: 'pin-no-configurado', error: error, title: 'PIN no configurado', userMessage: 'Configurá un PIN de supervisor en Reglas del POS.' });
          return false;
        }
        const pin = await promptSupervisorPin({
          message: 'Cerrar con diferencia requiere autorización. Ingresá el PIN de supervisor.',
        });
        if (!pin) return false;
        try {
          supervisorOverrideId = await authorizeSupervisorPin({
            branchId: currentBranchId,
            pin,
            module: 'Cash',
            action: 'correct',
            context: { sessionId: gateSessionId, difference },
          });
        } catch (e: unknown) {
          presentUnexpectedFailure({ module: 'cash', op: 'pin', error: e, title: 'PIN', userMessage: 'No se pudo autorizar.' });
          return false;
        }
      }
      const noteTrim = (closingNote ?? '').trim();
      if (!noteTrim) {
        showError(
          cashArqueoCopy.missingDifferenceNoteTitle,
          cashArqueoCopy.missingDifferenceNoteMessage
        );
        return false;
      }
      const confirmed = await confirm({
        title: difference > 0 ? cashArqueoCopy.surplusCloseTitle : cashArqueoCopy.shortageCloseTitle,
        message: formatCashDifferenceConfirmMessage(difference),
        confirmLabel: cashArqueoCopy.differenceConfirmCta,
        destructive: difference < 0,
        iconName: difference < 0 ? 'alert-circle-outline' : 'information-circle-outline',
      });
      if (!confirmed) return false;

      if (amount === 0) {
        const zeroConfirmed = await confirm({
          title: 'Cerrar con monto 0',
          message: cashArqueoCopy.zeroCountedConfirm,
          confirmLabel: 'Cerrar con 0',
          destructive: true,
          iconName: 'alert-circle-outline',
        });
        if (!zeroConfirmed) return false;
      }
      await runCloseCash(amount, supervisorOverrideId, freshSummary);
      return true;
    }

    if (amount === 0) {
      const confirmed = await confirm({
        title: 'Cerrar con monto 0',
        message: cashArqueoCopy.zeroCountedConfirm,
        confirmLabel: 'Cerrar con 0',
        destructive: true,
        iconName: 'alert-circle-outline',
      });
      if (!confirmed) return false;
      await runCloseCash(0, undefined, freshSummary);
      return true;
    }

    await runCloseCash(amount, undefined, freshSummary);
    return true;
  };

  const refreshActionData = useCallback(async (options?: { force?: boolean }) => {
    await Promise.all([
      outflowsFlow.reload(),
      options?.force ? activityFlow.onRefresh() : activityFlow.reload(),
    ]);
  }, [outflowsFlow.reload, activityFlow.onRefresh, activityFlow.reload]);

  return {
    openingAmount,
    setOpeningAmount,
    closingAmount,
    setClosingAmount,
    closingNote,
    setClosingNote,
    handleOpenCash,
    handleCloseCash,
    openingCash,
    closingCash,
    outflowsFlow,
    activityFlow,
    expenseRegisterSuppliers,
    activeCashSessionInfo,
    expenseRegisterSubmit,
    handleOpenRegisterOutflow,
    handleOpenRegisterInflow,
    resetCloseCashForm,
    refreshActionData,
  };
}
